/**
 * peer.js
 *
 * CHANGES vs original:
 * 1. TURN servers added — STUN-only fails silently behind symmetric NAT
 *    (the most common mobile/corporate setup). Without TURN, both users can
 *    be present on the socket but ontrack never fires and video never starts.
 * 2. reset() checks signalingState before closing (avoids InvalidStateError).
 * 3. setAnswer() guards against duplicate calls on rapid reconnect.
 * 4. addIceCandidate() errors are non-fatal warnings so a stale candidate
 *    can't kill an active call.
 */

class PeerService {
  constructor() {
    this.pc = null;

    // ── ICE config ────────────────────────────────────────────────────────────
    // OpenRelay is a free public TURN service by Metered.ca — no sign-up needed.
    // For production, replace with your own Twilio / Metered / Xirsys credentials.
    this.config = {
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
        { urls: "stun:stun2.l.google.com:19302" },
        { urls: "stun:stun3.l.google.com:19302" },
        { urls: "stun:stun4.l.google.com:19302" },

        // TURN — required when STUN-only ICE fails (symmetric NAT, mobile, corporate)
        {
          urls: "turn:openrelay.metered.ca:80",
          username: "openrelayproject",
          credential: "openrelayproject",
        },
        {
          urls: "turn:openrelay.metered.ca:443",
          username: "openrelayproject",
          credential: "openrelayproject",
        },
        {
          urls: "turn:openrelay.metered.ca:443?transport=tcp",
          username: "openrelayproject",
          credential: "openrelayproject",
        },

        // ── Your own TURN server (recommended for production) ─────────────────
        // {
        //   urls: "turn:your-turn-server.com:3478",
        //   username: "your-username",
        //   credential: "your-credential",
        // },
        // {
        //   urls: "turns:your-turn-server.com:5349",
        //   username: "your-username",
        //   credential: "your-credential",
        // },
      ],
      iceCandidatePoolSize: 10,
      iceTransportPolicy: "all", // change to "relay" to force TURN during debugging
    };

    this.init();
  }

  init() {
    try {
      this.pc = new RTCPeerConnection(this.config);
      console.log("✅ PeerConnection created");
    } catch (error) {
      console.error("❌ Error creating PeerConnection:", error);
      throw error;
    }
  }

  // Called ONLY from buildPeerConnection in Room.jsx — NOT from onOffer.
  // Calling reset() from onOffer was the double-reset bug that wiped the
  // ontrack handler set up by buildPeerConnection, preventing remote video.
  reset() {
    if (this.pc) {
      if (this.pc.signalingState !== "closed") {
        this.pc.close();
      }
      this.pc = null;
    }
    this.init();
    console.log("🔄 PeerConnection reset");
  }

  async createOffer() {
    try {
      if (!this.pc) this.init();
      const offer = await this.pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });
      await this.pc.setLocalDescription(offer);
      return offer;
    } catch (error) {
      console.error("❌ Error creating offer:", error);
      throw error;
    }
  }

  async createAnswer(offer) {
    try {
      if (!this.pc) this.init();
      await this.pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      return answer;
    } catch (error) {
      console.error("❌ Error creating answer:", error);
      throw error;
    }
  }

  async setAnswer(answer) {
    try {
      if (!this.pc) throw new Error("PeerConnection not initialized");
      if (this.pc.remoteDescription) {
        console.warn("⚠️ setAnswer: remoteDescription already set — ignoring duplicate");
        return;
      }
      await this.pc.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (error) {
      console.error("❌ Error setting answer:", error);
      throw error;
    }
  }

  async addIceCandidate(candidate) {
    try {
      if (!this.pc) throw new Error("PeerConnection not initialized");
      await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      // Non-fatal — a stale or out-of-order candidate must not kill the call
      console.warn("⚠️ addIceCandidate warning:", error.message);
    }
  }

  close() {
    if (this.pc) {
      if (this.pc.signalingState !== "closed") {
        this.pc.close();
      }
      this.pc = null;
      console.log("🔌 PeerConnection closed");
    }
  }

  getConnectionState() {
    return this.pc ? this.pc.connectionState : "closed";
  }

  getIceConnectionState() {
    return this.pc ? this.pc.iceConnectionState : "closed";
  }
}

let instance = null;

const getPeerInstance = () => {
  if (!instance) {
    instance = new PeerService();
  }
  return instance;
};

export default getPeerInstance();